import { useMutation, UseQueryOptions } from '@tanstack/react-query-v4'
import { ApiError, QueryKey } from '@api'
import { useApi } from '@api/useApi.hook'
import type { ApiGETFn } from '@api/standaloneApi'
import { useAuth } from '@domain/auth/Auth.context'
import { appConfig } from '@config'
import { useToast } from '@components/shared/toaster/toaster.ts'
import { queryClient } from 'src/providers/react-query'
import { isStatusCode403 } from '@utils/statusCode.utils'
import get from 'lodash/get'
import { invalidateServiceCache } from '@services/shared/cache.service.ts'

export const useUpdateMyAvatar = () => {
  const [auth] = useAuth()
  const { toast } = useToast()

  const { uploadFile } = useApi()
  const userUuid = get(auth, 'user.uuid')
  const avatarEndpoint = appConfig.endpoints.v1.users.avatar.replace(/:userId/, userUuid)
  const { mutate, isLoading } = useMutation(
    (uploadFile as ApiGETFn)<void, { avatar: File }>(avatarEndpoint),
  )
  const updateAvatar = (avatar: File, options?: UseQueryOptions) => {
    mutate(
      { avatar },
      {
        onSuccess: (data) => {
          if (options?.onSuccess) {
            options?.onSuccess(data)
          }
          toast({
            title: 'Your profile pic has been changed',
            variant: 'success',
          })
          invalidateServiceCache('me')
          queryClient.invalidateQueries({ queryKey: [QueryKey.Me] })
        },
        onError: (error) => {
          const err = error as ApiError
          if (!isStatusCode403(err.status)) {
            toast({ title: err.message, variant: 'error' })
          }
        },
      },
    )
  }

  return {
    updateAvatar,
    isLoading,
  }
}
